import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { fmtDate } from "@/pages/Events";
import {
  ArrowLeft, MapPin, CalendarHeart, Users, Ticket, Send, X, CheckCircle2,
  Circle, Trash2, Mail, ExternalLink, GraduationCap,
} from "lucide-react";

const STATUS_OPTIONS = ["invited", "attending", "maybe", "declined"];
const STATUS_STYLES = {
  invited: "bg-slate-100 text-slate-600",
  attending: "bg-emerald-100 text-emerald-800",
  maybe: "bg-amber-100 text-amber-800",
  declined: "bg-red-100 text-red-800",
};

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invitees, setInvitees] = useState(null); // legacy, unused
  const [allowResend, setAllowResend] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [opts, setOpts] = useState(null);
  const [audience, setAudience] = useState("all");
  const [selYears, setSelYears] = useState({});

  const load = () => {
    api.get(`/events/${id}`).then((r) => setEvent(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail))).finally(() => setLoading(false));
  };
  useEffect(load, [id]);

  const openInvite = async () => {
    try {
      const { data } = await api.get(`/events/${id}/invite-options`);
      setOpts(data);
      const evYears = data.event_class_years || [];
      if (evYears.length) {
        setAudience("classes");
        setSelYears(Object.fromEntries(evYears.map((y) => [y, true])));
      } else {
        setAudience("all");
        setSelYears({});
      }
      setAllowResend(false);
      setInviteOpen(true);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const chosenYears = () => (opts?.classes || []).filter((c) => selYears[c.class_year]).map((c) => c.class_year);
  const recipientCount = () => {
    if (!opts) return 0;
    if (audience === "all") return opts.total_all;
    return (opts.classes || []).filter((c) => selYears[c.class_year]).reduce((n, c) => n + c.count, 0);
  };

  const sendInvites = async () => {
    const years = chosenYears();
    if (audience === "classes" && years.length === 0) { toast.error("Select at least one class year"); return; }
    const count = recipientCount();
    if (count <= 0) { toast.error("No recipients match this audience"); return; }
    if (!window.confirm(`Send invitations to ${count} recipient(s)?`)) return;
    setInviting(true);
    try {
      const { data } = await api.post(`/events/${id}/invite${allowResend ? "?force=true" : ""}`, { audience, class_years: years });
      toast.success(`Invited ${data.sent}${data.skipped ? `, skipped ${data.skipped} already invited` : ""}${data.failed ? `, ${data.failed} failed` : ""}`);
      setInviteOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setInviting(false);
    }
  };

  const setRsvp = async (rid, patch) => {
    try {
      await api.put(`/events/${id}/rsvp/${rid}`, patch);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggleCheckin = async (rid) => {
    try { await api.post(`/events/${id}/rsvp/${rid}/checkin`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const removeRsvp = async (rid) => {
    if (!window.confirm("Remove this RSVP?")) return;
    try { await api.delete(`/events/${id}/rsvp/${rid}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (loading) return <div className="text-muted-foreground">Loading…</div>;
  if (!event) return <div className="text-muted-foreground">Event not found.</div>;

  const s = event.summary || {};
  const rsvps = event.rsvps || [];
  const loc = event.location || {};

  return (
    <div className="fade-up">
      <button data-testid="back-to-events" onClick={() => navigate("/events")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-4">
        <ArrowLeft className="h-4 w-4" /> Back to Events
      </button>

      <div className="bg-card border border-border rounded-lg shadow-sm p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded bg-accent/20 text-primary"><Ticket className="h-3 w-3" /> {event.category}</span>
              <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded bg-slate-100 text-slate-600">{event.status}</span>
            </div>
            <h1 className="font-serif text-3xl tracking-tight flex items-center gap-2"><CalendarHeart className="h-7 w-7 text-primary shrink-0" />{event.title}</h1>
            <div className="text-sm text-muted-foreground mt-2">{fmtDate(event.start)}{event.end ? ` — ${fmtDate(event.end)}` : ""}</div>
            {(loc.venue || loc.city) && (
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1"><MapPin className="h-4 w-4" />{[loc.venue, loc.address, loc.city, loc.state].filter(Boolean).join(", ")}</div>
            )}
            {loc.virtual_url && <a href={loc.virtual_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-sm text-primary mt-1 hover:underline"><ExternalLink className="h-3.5 w-3.5" />{loc.virtual_url}</a>}
            {event.description && <p className="text-sm mt-3 max-w-2xl leading-relaxed">{event.description}</p>}
            <div className="flex gap-4 mt-3 text-sm text-muted-foreground">
              {event.ticket_price ? <span>Ticket: <b className="text-foreground">${event.ticket_price.toFixed(2)}</b></span> : <span>Free event</span>}
              {event.capacity ? <span>Capacity: <b className="text-foreground">{event.capacity}</b></span> : null}
            </div>
          </div>
          {isAdmin && (
            <div className="flex flex-col gap-2 shrink-0">
              <button data-testid="invite-btn" onClick={openInvite} className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90">
                <Send className="h-4 w-4" /> Send Invitations
              </button>
              <button data-testid="invite-faculty-btn" onClick={() => navigate(`/faculty?invite=${id}`)} className="flex items-center gap-2 px-4 py-2.5 rounded-md border border-border text-sm font-medium hover:bg-muted">
                <GraduationCap className="h-4 w-4" /> Invite Faculty &amp; Staff
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-6 pt-6 border-t border-border">
          {[["Invited", s.invited], ["Attending", s.attending], ["Maybe", s.maybe], ["Declined", s.declined], ["Checked-in", s.checked_in]].map(([l, v]) => (
            <div key={l} className="text-center" data-testid={`stat-${l.toLowerCase()}`}>
              <div className="text-2xl font-semibold">{v || 0}</div>
              <div className="text-[10px] font-mono uppercase tracking-wide text-muted-foreground">{l}</div>
            </div>
          ))}
        </div>
        {s.headcount ? <div className="text-xs text-muted-foreground mt-3">Expected headcount incl. guests: <b className="text-foreground">{s.headcount}</b></div> : null}
      </div>

      <div className="mt-6 bg-card border border-border rounded-lg shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-border flex items-center gap-2">
          <Users className="h-4 w-4 text-primary" />
          <span className="font-serif text-lg tracking-tight">Guest List</span>
          <span className="text-sm text-muted-foreground">({rsvps.length})</span>
        </div>
        {rsvps.length === 0 && <div className="px-5 py-8 text-sm text-muted-foreground text-center" data-testid="rsvp-empty">No one has been invited yet. Use “Send Invitations” to reach alumni.</div>}
        {rsvps.length > 0 && (
          <div className="divide-y divide-border">
            {rsvps.map((r) => (
              <div key={r.id} data-testid={`rsvp-row-${r.id}`} className="px-5 py-3 flex items-center gap-3 flex-wrap hover:bg-muted/40">
                {isAdmin && (
                  <button data-testid={`checkin-${r.id}`} onClick={() => toggleCheckin(r.id)} title="Toggle check-in" className={r.checked_in ? "text-emerald-600" : "text-muted-foreground hover:text-primary"}>
                    {r.checked_in ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5" />}
                  </button>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{r.name || "—"}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1 truncate"><Mail className="h-3 w-3" />{r.email || "no email"}{r.class_year ? ` · ’${String(r.class_year).slice(-2)}` : ""}</div>
                </div>
                {r.guests ? <span className="text-xs text-muted-foreground">+{r.guests} guest{r.guests > 1 ? "s" : ""}</span> : null}
                {isAdmin ? (
                  <select data-testid={`rsvp-status-${r.id}`} value={r.status} onChange={(e) => setRsvp(r.id, { status: e.target.value })} className={`text-xs font-medium px-2 py-1 rounded border-0 ${STATUS_STYLES[r.status] || STATUS_STYLES.invited}`}>
                    {STATUS_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <span className={`text-xs font-medium px-2 py-1 rounded ${STATUS_STYLES[r.status] || STATUS_STYLES.invited}`}>{r.status}</span>
                )}
                {isAdmin && <input data-testid={`rsvp-guests-${r.id}`} type="number" min="0" value={r.guests || 0} onChange={(e) => setRsvp(r.id, { guests: parseInt(e.target.value || "0", 10) })} className="w-14 px-2 py-1 rounded border border-input text-xs" title="Guests" />}
                {isAdmin && <button data-testid={`rsvp-delete-${r.id}`} onClick={() => removeRsvp(r.id)} className="text-muted-foreground hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
              </div>
            ))}
          </div>
        )}
      </div>

      {inviteOpen && opts && createPortal((
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-slate-900/60" onMouseDown={(ev) => { if (ev.target === ev.currentTarget) setInviteOpen(false); }}>
          <div className="bg-card w-full max-w-lg rounded-lg shadow-xl border border-border flex flex-col" style={{ maxHeight: "calc(100vh - 2rem)", overflow: "hidden" }}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Send Invitations</div>
                <h2 className="font-serif text-lg tracking-tight">{event.title}</h2>
              </div>
              <button data-testid="invite-close" onClick={() => setInviteOpen(false)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 overflow-y-auto flex-1 min-h-0 space-y-4">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Audience</div>
                <div className="space-y-2">
                  <label data-testid="audience-all" className={`flex items-center gap-3 px-4 py-3 rounded-md border cursor-pointer transition-colors ${audience === "all" ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"}`}>
                    <input type="radio" name="audience" checked={audience === "all"} onChange={() => setAudience("all")} className="h-4 w-4 accent-[#001A4B]" />
                    <span className="flex-1">
                      <span className="font-medium block">Everyone</span>
                      <span className="text-xs text-muted-foreground">All alumni with a valid email who accept event invitations</span>
                    </span>
                    <span data-testid="audience-all-count" className="text-sm font-semibold text-primary">{opts.total_all}</span>
                  </label>
                  <label data-testid="audience-classes" className={`flex items-center gap-3 px-4 py-3 rounded-md border cursor-pointer transition-colors ${audience === "classes" ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"}`}>
                    <input type="radio" name="audience" checked={audience === "classes"} onChange={() => setAudience("classes")} className="h-4 w-4 accent-[#001A4B]" />
                    <span className="flex-1">
                      <span className="font-medium block">Specific class year(s)</span>
                      <span className="text-xs text-muted-foreground">Target one or more graduating classes</span>
                    </span>
                  </label>
                </div>
              </div>

              {audience === "classes" && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Class Years</span>
                    <div className="flex gap-3 text-xs">
                      <button type="button" data-testid="years-select-all" onClick={() => setSelYears(Object.fromEntries((opts.classes || []).map((c) => [c.class_year, true])))} className="text-primary hover:underline">Select all</button>
                      <button type="button" data-testid="years-clear" onClick={() => setSelYears({})} className="text-muted-foreground hover:underline">Clear</button>
                    </div>
                  </div>
                  <div className="border border-border rounded-md max-h-56 overflow-y-auto divide-y divide-border">
                    {(opts.classes || []).length === 0 && <div className="px-4 py-4 text-sm text-muted-foreground">No alumni with an email are on file yet.</div>}
                    {(opts.classes || []).map((c) => (
                      <label key={c.class_year} data-testid={`year-${c.class_year}`} className="flex items-center gap-3 px-4 py-2 hover:bg-muted/50 cursor-pointer text-sm">
                        <input type="checkbox" data-testid={`year-check-${c.class_year}`} checked={!!selYears[c.class_year]} onChange={(e) => setSelYears((p) => ({ ...p, [c.class_year]: e.target.checked }))} className="h-4 w-4 accent-[#001A4B]" />
                        <span className="flex-1 font-medium">Class of {c.class_year}</span>
                        <span className="text-xs text-muted-foreground">{c.count} recipient{c.count === 1 ? "" : "s"}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-3 border-t border-border flex items-center justify-between gap-3 shrink-0">
              <label data-testid="invite-allow-resend" className="flex items-center gap-2 text-xs cursor-pointer select-none">
                <input type="checkbox" data-testid="invite-resend-check" checked={allowResend} onChange={(e) => setAllowResend(e.target.checked)} className="h-4 w-4 accent-[#001A4B]" />
                <span className="font-medium text-foreground">Allow resend</span>
                <span className="text-muted-foreground">— re-invite even if already invited</span>
              </label>
              <button data-testid="invite-send" onClick={sendInvites} disabled={inviting || recipientCount() === 0} className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-60 shrink-0">
                <Send className="h-4 w-4" /> {inviting ? "Sending…" : `Send to ${recipientCount()}`}
              </button>
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}
